import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

/**
 * Tenant provisioning API — the "provision tenant" step that runs BEFORE a
 * company is added. Creates the tenant DB + S3 credentials and reports status.
 * Mirrors companyApi.ts conventions (Bearer token, APIError, POST endpoints).
 */

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export type ProvisionStatus =
  | "provisioning"
  | "ready"
  | "active"
  | "inactive"
  | "failed";

export interface TenantConfigDTO {
  id: number;
  tenant_id: string;
  company_id: number | null;
  db_host: string | null;
  db_name: string | null;
  db_user: string | null;
  db_password: string | null; // masked ("••••••••") from list/provision
  db_secret_ref: string | null;
  s3_bucket: string | null;
  s3_region: string | null;
  s3_access_key_id: string | null;
  s3_secret_access_key: string | null; // masked unless get() reveals it
  provision_driver: string | null;
  provision_error: string | null;
  status: ProvisionStatus;
  created_at: string | null;
  updated_at: string | null;
}

interface ProvisionResponseDTO {
  success: boolean;
  message: string;
  data: { config: TenantConfigDTO };
}

interface ListConfigsResponseDTO {
  success: boolean;
  message: string;
  data: { configs: TenantConfigDTO[] };
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new APIError(
        payload.message || `HTTP error! status: ${response.status}`,
        response.status,
        payload
      );
    }

    return payload as T;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
}

// List all tenant provisioning configs (newest first).
export const listTenantConfigs = async (): Promise<ListConfigsResponseDTO> =>
  postJson<ListConfigsResponseDTO>("/api/provisioning/list", {});

// Provision (create DB + S3 credentials) for a subdomain / tenant id.
export const provisionTenant = async (
  tenantId: string,
  s3Region?: string
): Promise<ProvisionResponseDTO> =>
  postJson<ProvisionResponseDTO>("/api/provisioning/provision", {
    tenant_id: tenantId,
    ...(s3Region ? { s3_region: s3Region } : {}),
  });

// Retry provisioning for a failed tenant (same as provision under the hood).
export const retryTenant = async (
  tenantId: string
): Promise<ProvisionResponseDTO> =>
  postJson<ProvisionResponseDTO>("/api/provisioning/retry", {
    tenant_id: tenantId,
  });

export { APIError as ProvisioningAPIError };
